import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import styles from './StatReportForm.module.css';
import commonStyles from '../common/CommonStyles.module.css';

const StatReportForm = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [error, setError] = useState('');
  const [federalDistricts, setFederalDistricts] = useState([]);
  const [regions, setRegions] = useState([]);
  const [selectedDistrictId, setSelectedDistrictId] = useState('');
  const [filteredRegions, setFilteredRegions] = useState([]);
  const submitLockRef = useRef(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showAllErrors, setShowAllErrors] = useState(false);

  const [formData, setFormData] = useState({
    federalDistrictId: '',
    regionId: '',
    primaryDivisionsCount: '',
    localDivisionsCount: '',
    membersTotal: '',
    membersUnder18: '',
    membersOver35: '',
    averageAge: '',
    membersEmployees: '',
    membersWorkers: '',
    membersStudents: '',
    membersSchoolchildren: '',
    membersUnemployed: '',
    membersPayingDues: '',
    membersDepartedTotal: '',
    membersExcluded: '',
    membersJoined: '',
    committeeMembersCount: '',
    bureauMembersCount: '',
    secretariesCount: '',
    staffWorkersCount: '',
    deputiesRegionalLegislative: '',
    deputiesLocalRepresentative: '',
    assistantsPermanent: '',
    assistantsVoluntary: '',
    youthCouncilRegional: '',
    youthCouncilLocal: '',
    pioneerMembersCount: '',
    discourseMembersCount: '',
    studyCirclesTotal: '',
    studyCirclesAttendees: '',
    hasWebsite: false,
    socialNetworksCount: '',
    socialTelegram: false,
    socialVk: false,
    socialOk: false,
    socialTiktok: false,
    socialMax: false,
    socialOther: false,
    committeeAddress: '',
    committeeEmail: '',
    vkGroupLink: '',
    telegramChannelLink: '',
    otherSocialLinks: '',
    firstSecretaryName: ''
  });

  const resetForm = () => {
    setFormData({
      federalDistrictId: '',
      regionId: '',
      primaryDivisionsCount: '',
      localDivisionsCount: '',
      membersTotal: '',
      membersUnder18: '',
      membersOver35: '',
      averageAge: '',
      membersEmployees: '',
      membersWorkers: '',
      membersStudents: '',
      membersSchoolchildren: '',
      membersUnemployed: '',
      membersPayingDues: '',
      membersDepartedTotal: '',
      membersExcluded: '',
      membersJoined: '',
      committeeMembersCount: '',
      bureauMembersCount: '',
      secretariesCount: '',
      staffWorkersCount: '',
      deputiesRegionalLegislative: '',
      deputiesLocalRepresentative: '',
      assistantsPermanent: '',
      assistantsVoluntary: '',
      youthCouncilRegional: '',
      youthCouncilLocal: '',
      pioneerMembersCount: '',
      discourseMembersCount: '',
      studyCirclesTotal: '',
      studyCirclesAttendees: '',
      hasWebsite: false,
      socialNetworksCount: '',
      socialTelegram: false,
      socialVk: false,
      socialOk: false,
      socialTiktok: false,
      socialMax: false,
      socialOther: false,
      committeeAddress: '',
      committeeEmail: '',
      vkGroupLink: '',
      telegramChannelLink: '',
      otherSocialLinks: '',
      firstSecretaryName: ''
    });
    setSelectedDistrictId('');
    setValidationErrors({});
    setShowAllErrors(false);
  };

  useEffect(() => {
    fetchReferenceData();
  }, []);

  useEffect(() => {
    if (selectedDistrictId) {
      const filtered = regions.filter(r => r.federalDistrict.districtId === parseInt(selectedDistrictId));
      setFilteredRegions(filtered);
      setFormData(prev => ({ ...prev, regionId: '' }));
    } else {
      setFilteredRegions([]);
    }
  }, [selectedDistrictId, regions]);

  useEffect(() => {
    // Скроллинг к первой ошибке при их появлении
    if (showAllErrors && Object.keys(validationErrors).length > 0) {
      const firstErrorElement = document.querySelector(`.${styles.inputError}`);
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        // Фокусируемся на первом ошибочном поле
        firstErrorElement.focus();
      }
    }
  }, [validationErrors, showAllErrors]);

  const fetchReferenceData = async () => {
    try {
      const response = await axios.get('/api/stat-reports/reference-data');
      setFederalDistricts(response.data.federalDistricts);
      setRegions(response.data.regions);
      setLoading(false);
    } catch (error) {
      console.error('Ошибка при загрузке справочных данных:', error);
      setError('Не удалось загрузить справочные данные');
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    // Проверка всех обязательных полей
    const requiredFields = {
      federalDistrictId: 'Федеральный округ',
      regionId: 'Регион',
      primaryDivisionsCount: 'Количество первичных отделений',
      localDivisionsCount: 'Количество местных отделений',
      membersTotal: 'Количество членов ЛКСМ РФ',
      membersUnder18: 'Количество членов ЛКСМ РФ до 18 лет',
      membersOver35: 'Количество членов ЛКСМ РФ старше 35 лет',
      averageAge: 'Средний возраст членов отделения',
      membersEmployees: 'Количество служащих',
      membersWorkers: 'Количество рабочих',
      membersStudents: 'Количество студентов',
      membersSchoolchildren: 'Количество школьников',
      membersUnemployed: 'Количество безработных',
      membersPayingDues: 'Количество членов, уплачивающих взносы',
      membersDepartedTotal: 'Количество выбывших',
      membersExcluded: 'Количество исключённых',
      membersJoined: 'Количество вступивших',
      committeeMembersCount: 'Количество членов Комитета',
      bureauMembersCount: 'Количество членов Бюро',
      secretariesCount: 'Количество секретарей',
      staffWorkersCount: 'Количество штатных работников',
      deputiesRegionalLegislative: 'Количество депутатов в законодательном органе субъекта',
      deputiesLocalRepresentative: 'Количество депутатов в органах местного самоуправления',
      assistantsPermanent: 'Количество помощников депутатов на постоянной основе',
      assistantsVoluntary: 'Количество помощников депутатов на общественных началах',
      youthCouncilRegional: 'Количество членов молодёжных советов при законодательном органе',
      youthCouncilLocal: 'Количество членов молодёжных советов при органах местного самоуправления',
      pioneerMembersCount: 'Количество членов пионерской организации',
      discourseMembersCount: 'Количество членов НСП «Дискурс»',
      studyCirclesTotal: 'Количество кружков, школ, семинаров, лекториев',
      studyCirclesAttendees: 'Количество посещающих занятия',
      socialNetworksCount: 'Количество подписчиков',
      committeeAddress: 'Адрес регионального комитета',
      committeeEmail: 'Почта регионального отделения',
      vkGroupLink: 'Ссылка на группу в ВКонтакте',
      firstSecretaryName: 'ФИО первого секретаря'
    };

    // Проверяем каждое обязательное поле
    Object.entries(requiredFields).forEach(([field, fieldName]) => {
      const value = formData[field];

      if (field === 'committeeEmail') {
        // Валидация email
        if (!value || !value.trim()) {
          errors[field] = `${fieldName} обязателен для заполнения`;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          errors[field] = 'Введите корректный email адрес';
        }
      } else if (field === 'averageAge') {
        // Валидация среднего возраста
        if (!value || value.trim() === '') {
          errors[field] = `${fieldName} обязательно для заполнения`;
        } else {
          const numValue = parseFloat(value);
          if (isNaN(numValue) || numValue < 0 || numValue > 100) {
            errors[field] = 'Введите корректный возраст (0-100)';
          }
        }
      } else if (field.includes('Count') || field.includes('Total') ||
          field.includes('members') || field.includes('deputies') ||
          field.includes('assistants') || field.includes('youthCouncil') ||
          field.includes('secretaries') || field.includes('staffWorkers') ||
          field.includes('studyCircles') || field.includes('socialNetworksCount') ||
          field.includes('Divisions')) {
        // Валидация числовых полей
        if (!value || value.trim() === '') {
          errors[field] = `${fieldName} обязательно для заполнения`;
        } else {
          const numValue = parseInt(value);
          if (isNaN(numValue) || numValue < 0) {
            errors[field] = 'Введите положительное число или 0';
          }
        }
      } else if (!value || value.trim() === '') {
        // Валидация текстовых полей
        errors[field] = `${fieldName} обязательно для заполнения`;
      }
    });

    // Дополнительная проверка: сумма социальных статусов не должна превышать общее количество членов
    if (formData.membersTotal && formData.membersEmployees && formData.membersWorkers &&
        formData.membersStudents && formData.membersSchoolchildren && formData.membersUnemployed) {
      const socialSum = parseInt(formData.membersEmployees || 0) +
          parseInt(formData.membersWorkers || 0) +
          parseInt(formData.membersStudents || 0) +
          parseInt(formData.membersSchoolchildren || 0) +
          parseInt(formData.membersUnemployed || 0);
      const total = parseInt(formData.membersTotal || 0);

      if (socialSum > total) {
        errors.membersTotal = 'Сумма по социальным статусам не может превышать общее количество членов';
      }
    }

    // Дополнительная проверка: количество выбывших должно быть >= количества исключённых
    if (formData.membersDepartedTotal && formData.membersExcluded) {
      const departed = parseInt(formData.membersDepartedTotal);
      const excluded = parseInt(formData.membersExcluded);
      if (excluded > departed) {
        errors.membersExcluded = 'Количество исключённых не может превышать общее количество выбывших';
      }
    }

    return errors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Очищаем ошибку валидации при изменении поля
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleDistrictChange = (e) => {
    setSelectedDistrictId(e.target.value);
    setFormData(prev => ({
      ...prev,
      federalDistrictId: e.target.value,
      regionId: ''
    }));

    // Очищаем ошибки связанные с выбором региона
    if (validationErrors.regionId) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.regionId;
        return newErrors;
      });
    }
  };

  // Функция для предотвращения изменения значений колесом мыши
  const handleWheel = (e) => {
    e.target.blur();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitLockRef.current) return;

    // Валидация формы
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setShowAllErrors(true);
      setError('Пожалуйста, заполните все обязательные поля корректно. Форма прокручена к первой ошибке.');
      return;
    }

    submitLockRef.current = true;
    setSubmitting(true);
    setError('');
    setShowAllErrors(false);

    try {
      const payload = {
        ...formData,
        federalDistrictId: parseInt(formData.federalDistrictId),
        regionId: parseInt(formData.regionId),
        primaryDivisionsCount: formData.primaryDivisionsCount ? parseInt(formData.primaryDivisionsCount) : null,
        localDivisionsCount: formData.localDivisionsCount ? parseInt(formData.localDivisionsCount) : null,
        membersTotal: formData.membersTotal ? parseInt(formData.membersTotal) : null,
        membersUnder18: formData.membersUnder18 ? parseInt(formData.membersUnder18) : null,
        membersOver35: formData.membersOver35 ? parseInt(formData.membersOver35) : null,
        averageAge: formData.averageAge ? parseFloat(formData.averageAge) : null,
        membersEmployees: formData.membersEmployees ? parseInt(formData.membersEmployees) : null,
        membersWorkers: formData.membersWorkers ? parseInt(formData.membersWorkers) : null,
        membersStudents: formData.membersStudents ? parseInt(formData.membersStudents) : null,
        membersSchoolchildren: formData.membersSchoolchildren ? parseInt(formData.membersSchoolchildren) : null,
        membersUnemployed: formData.membersUnemployed ? parseInt(formData.membersUnemployed) : null,
        membersPayingDues: formData.membersPayingDues ? parseInt(formData.membersPayingDues) : null,
        membersDepartedTotal: formData.membersDepartedTotal ? parseInt(formData.membersDepartedTotal) : null,
        membersExcluded: formData.membersExcluded ? parseInt(formData.membersExcluded) : null,
        membersJoined: formData.membersJoined ? parseInt(formData.membersJoined) : null,
        committeeMembersCount: formData.committeeMembersCount ? parseInt(formData.committeeMembersCount) : null,
        bureauMembersCount: formData.bureauMembersCount ? parseInt(formData.bureauMembersCount) : null,
        secretariesCount: formData.secretariesCount ? parseInt(formData.secretariesCount) : null,
        staffWorkersCount: formData.staffWorkersCount ? parseInt(formData.staffWorkersCount) : null,
        deputiesRegionalLegislative: formData.deputiesRegionalLegislative ? parseInt(formData.deputiesRegionalLegislative) : null,
        deputiesLocalRepresentative: formData.deputiesLocalRepresentative ? parseInt(formData.deputiesLocalRepresentative) : null,
        assistantsPermanent: formData.assistantsPermanent ? parseInt(formData.assistantsPermanent) : null,
        assistantsVoluntary: formData.assistantsVoluntary ? parseInt(formData.assistantsVoluntary) : null,
        youthCouncilRegional: formData.youthCouncilRegional ? parseInt(formData.youthCouncilRegional) : null,
        youthCouncilLocal: formData.youthCouncilLocal ? parseInt(formData.youthCouncilLocal) : null,
        pioneerMembersCount: formData.pioneerMembersCount ? parseInt(formData.pioneerMembersCount) : null,
        discourseMembersCount: formData.discourseMembersCount ? parseInt(formData.discourseMembersCount) : null,
        studyCirclesTotal: formData.studyCirclesTotal ? parseInt(formData.studyCirclesTotal) : null,
        studyCirclesAttendees: formData.studyCirclesAttendees ? parseInt(formData.studyCirclesAttendees) : null,
        socialNetworksCount: formData.socialNetworksCount ? parseInt(formData.socialNetworksCount) : null
      };

      await axios.post('/api/stat-reports', payload);
      resetForm();
      setIsSuccessModalOpen(true);
    } catch (error) {
      console.error('Ошибка при отправке формы:', error);
      setError(error.response?.data?.message || 'Ошибка при отправке формы');
    } finally {
      setSubmitting(false);
      submitLockRef.current = false;
    }
  };

  if (loading) {
    return (
        <div className={commonStyles.pageContainer}>
          <div className={commonStyles.contentContainer}>
            <div className={commonStyles.content}>
              <div className={styles.loading}>Загрузка данных...</div>
            </div>
          </div>
        </div>
    );
  }

  return (
      <div className={commonStyles.pageContainer}>
        <div className={commonStyles.contentContainer}>
          <div className={commonStyles.content}>
            <h1 className={styles.title}>Форма статистического отчета</h1>
            <h3 className={styles.title}>Данные заполнять на 1 января 2026 года</h3>

            {error && (
                <div className={styles.errorMessage}>
                  {error}
                  {validationErrors && Object.keys(validationErrors).length > 0
                      && (
                          <div className={styles.errorCount}>
                            Найдено {Object.keys(
                              validationErrors).length} ошибок
                          </div>
                      )}
                </div>
            )}

            <form onSubmit={handleSubmit} className={styles.form} noValidate>
              {/* Основная информация */}
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Основная информация</h3>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Федеральный округ <span className={styles.required}>*</span>
                  </label>
                  <select
                      name="federalDistrictId"
                      value={formData.federalDistrictId}
                      onChange={handleDistrictChange}
                      className={`${styles.select} ${validationErrors.federalDistrictId
                          ? styles.inputError : ''}`}
                      required
                  >
                    <option value="">Выберите федеральный округ</option>
                    {federalDistricts.map(district => (
                        <option key={district.districtId}
                                value={district.districtId}>
                          {district.name}
                        </option>
                    ))}
                  </select>
                  {validationErrors.federalDistrictId && (
                      <div
                          className={styles.validationError}>{validationErrors.federalDistrictId}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Регион <span className={styles.required}>*</span>
                  </label>
                  <select
                      name="regionId"
                      value={formData.regionId}
                      onChange={handleChange}
                      className={`${styles.select} ${validationErrors.regionId
                          ? styles.inputError : ''}`}
                      required
                      disabled={!selectedDistrictId}
                  >
                    <option value="">Выберите регион</option>
                    {filteredRegions.map(region => (
                        <option key={region.regionId} value={region.regionId}>
                          {region.name}
                        </option>
                    ))}
                  </select>
                  {validationErrors.regionId && (
                      <div
                          className={styles.validationError}>{validationErrors.regionId}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Количество первичных отделений <span
                      className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="primaryDivisionsCount"
                      value={formData.primaryDivisionsCount}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.primaryDivisionsCount
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.primaryDivisionsCount && (
                      <div
                          className={styles.validationError}>{validationErrors.primaryDivisionsCount}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Количество местных отделений <span
                      className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="localDivisionsCount"
                      value={formData.localDivisionsCount}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.localDivisionsCount
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.localDivisionsCount && (
                      <div
                          className={styles.validationError}>{validationErrors.localDivisionsCount}</div>
                  )}
                </div>
              </section>

              {/* Члены ЛКСМ РФ */}
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Члены ЛКСМ РФ</h3>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Количество членов ЛКСМ РФ <span
                      className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="membersTotal"
                      value={formData.membersTotal}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.membersTotal
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.membersTotal && (
                      <div
                          className={styles.validationError}>{validationErrors.membersTotal}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Количество членов ЛКСМ РФ до 18 лет <span
                      className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="membersUnder18"
                      value={formData.membersUnder18}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.membersUnder18
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.membersUnder18 && (
                      <div
                          className={styles.validationError}>{validationErrors.membersUnder18}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Количество членов ЛКСМ РФ старше 35 лет <span
                      className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="membersOver35"
                      value={formData.membersOver35}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.membersOver35
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.membersOver35 && (
                      <div
                          className={styles.validationError}>{validationErrors.membersOver35}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Средний возраст членов отделения <span
                      className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="averageAge"
                      value={formData.averageAge}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.averageAge
                          ? styles.inputError : ''}`}
                      min="0"
                      step="0.01"
                      required
                  />
                  {validationErrors.averageAge && (
                      <div
                          className={styles.validationError}>{validationErrors.averageAge}</div>
                  )}
                </div>
              </section>

              {/* Социальный статус */}
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Количество членов ЛКСМ РФ по
                  социальному статусу</h3>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Служащие <span className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="membersEmployees"
                      value={formData.membersEmployees}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.membersEmployees
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.membersEmployees && (
                      <div
                          className={styles.validationError}>{validationErrors.membersEmployees}</div>
                  )}
                  <small className={styles.hint}>Служащий - работник,
                    выполняющий административные, управленческие,
                    интеллектуальные функции, занятый в основном умственным
                    трудом</small>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Рабочие <span className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="membersWorkers"
                      value={formData.membersWorkers}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.membersWorkers
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.membersWorkers && (
                      <div
                          className={styles.validationError}>{validationErrors.membersWorkers}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Студенты <span className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="membersStudents"
                      value={formData.membersStudents}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.membersStudents
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.membersStudents && (
                      <div
                          className={styles.validationError}>{validationErrors.membersStudents}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Школьники <span className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="membersSchoolchildren"
                      value={formData.membersSchoolchildren}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.membersSchoolchildren
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.membersSchoolchildren && (
                      <div
                          className={styles.validationError}>{validationErrors.membersSchoolchildren}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Безработные <span className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="membersUnemployed"
                      value={formData.membersUnemployed}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.membersUnemployed
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.membersUnemployed && (
                      <div
                          className={styles.validationError}>{validationErrors.membersUnemployed}</div>
                  )}
                </div>
              </section>

              {/* Взносы и движение */}
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Взносы и движение</h3>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Количество членов ЛКСМ РФ, уплачивающих членские
                    взносы <span className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="membersPayingDues"
                      value={formData.membersPayingDues}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.membersPayingDues
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.membersPayingDues && (
                      <div
                          className={styles.validationError}>{validationErrors.membersPayingDues}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Количество выбывших из ЛКСМ РФ за отчётный период
                    (Всего) <span className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="membersDepartedTotal"
                      value={formData.membersDepartedTotal}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.membersDepartedTotal
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.membersDepartedTotal && (
                      <div
                          className={styles.validationError}>{validationErrors.membersDepartedTotal}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Из них: Исключённых из ЛКСМ РФ <span
                      className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="membersExcluded"
                      value={formData.membersExcluded}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.membersExcluded
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.membersExcluded && (
                      <div
                          className={styles.validationError}>{validationErrors.membersExcluded}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Количество вступивших в ЛКСМ РФ за отчётный период <span
                      className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="membersJoined"
                      value={formData.membersJoined}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.membersJoined
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.membersJoined && (
                      <div
                          className={styles.validationError}>{validationErrors.membersJoined}</div>
                  )}
                </div>
              </section>

              {/* Комитет */}
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Комитет регионального
                  отделения</h3>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Количество членов Комитета регионального отделения ЛКСМ
                    РФ <span className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="committeeMembersCount"
                      value={formData.committeeMembersCount}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.committeeMembersCount
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.committeeMembersCount && (
                      <div
                          className={styles.validationError}>{validationErrors.committeeMembersCount}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Количество членов Бюро Комитета регионального отделения ЛКСМ
                    РФ <span className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="bureauMembersCount"
                      value={formData.bureauMembersCount}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.bureauMembersCount
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.bureauMembersCount && (
                      <div
                          className={styles.validationError}>{validationErrors.bureauMembersCount}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Количество секретарей Комитета регионального отделения ЛКСМ
                    РФ <span className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="secretariesCount"
                      value={formData.secretariesCount}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.secretariesCount
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.secretariesCount && (
                      <div
                          className={styles.validationError}>{validationErrors.secretariesCount}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Количество штатных работников Комитета регионального
                    отделения ЛКСМ РФ <span className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="staffWorkersCount"
                      value={formData.staffWorkersCount}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.staffWorkersCount
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.staffWorkersCount && (
                      <div
                          className={styles.validationError}>{validationErrors.staffWorkersCount}</div>
                  )}
                </div>
              </section>

              {/* Депутаты */}
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Депутаты</h3>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Количество депутатов - членов ЛКСМ РФ в законодательном
                    (представительном) органе власти субъекта Российской
                    Федерации <span className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="deputiesRegionalLegislative"
                      value={formData.deputiesRegionalLegislative}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.deputiesRegionalLegislative
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.deputiesRegionalLegislative && (
                      <div
                          className={styles.validationError}>{validationErrors.deputiesRegionalLegislative}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Количество депутатов - членов ЛКСМ РФ в представительных
                    органах местного самоуправления <span
                      className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="deputiesLocalRepresentative"
                      value={formData.deputiesLocalRepresentative}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.deputiesLocalRepresentative
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.deputiesLocalRepresentative && (
                      <div
                          className={styles.validationError}>{validationErrors.deputiesLocalRepresentative}</div>
                  )}
                </div>
              </section>

              {/* Помощники депутатов */}
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Помощники депутатов</h3>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Количество помощников депутатов на постоянной, штатной
                    основе <span className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="assistantsPermanent"
                      value={formData.assistantsPermanent}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.assistantsPermanent
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.assistantsPermanent && (
                      <div
                          className={styles.validationError}>{validationErrors.assistantsPermanent}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Количество помощников депутатов на общественных
                    началах <span className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="assistantsVoluntary"
                      value={formData.assistantsVoluntary}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.assistantsVoluntary
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.assistantsVoluntary && (
                      <div
                          className={styles.validationError}>{validationErrors.assistantsVoluntary}</div>
                  )}
                </div>
              </section>

              {/* Молодёжные советы */}
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Молодёжные консультативные
                  совещательные органы</h3>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Количество членов молодёжных консультативных совещательных
                    органов при законодательном (представительном) органе власти
                    субъекта Российской Федерации <span
                      className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="youthCouncilRegional"
                      value={formData.youthCouncilRegional}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.youthCouncilRegional
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.youthCouncilRegional && (
                      <div
                          className={styles.validationError}>{validationErrors.youthCouncilRegional}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Количество членов молодёжных консультативных совещательных
                    органов при представительных органах местного
                    самоуправления <span className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="youthCouncilLocal"
                      value={formData.youthCouncilLocal}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.youthCouncilLocal
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.youthCouncilLocal && (
                      <div
                          className={styles.validationError}>{validationErrors.youthCouncilLocal}</div>
                  )}
                </div>
              </section>

              {/* Другие организации */}
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Другие организации</h3>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Количество членов пионерской организации <span
                      className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="pioneerMembersCount"
                      value={formData.pioneerMembersCount}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.pioneerMembersCount
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.pioneerMembersCount && (
                      <div
                          className={styles.validationError}>{validationErrors.pioneerMembersCount}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Количество членов НСП «Дискурс» <span
                      className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="discourseMembersCount"
                      value={formData.discourseMembersCount}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.discourseMembersCount
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.discourseMembersCount && (
                      <div
                          className={styles.validationError}>{validationErrors.discourseMembersCount}</div>
                  )}
                </div>
              </section>

              {/* Комсомольская учёба */}
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Комсомольская учёба</h3>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Количество кружков, школ, семинаров, лекториев в сети
                    комсомольской учебы (Всего) <span
                      className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="studyCirclesTotal"
                      value={formData.studyCirclesTotal}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.studyCirclesTotal
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.studyCirclesTotal && (
                      <div
                          className={styles.validationError}>{validationErrors.studyCirclesTotal}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Количество посещающих занятия <span
                      className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="studyCirclesAttendees"
                      value={formData.studyCirclesAttendees}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.studyCirclesAttendees
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.studyCirclesAttendees && (
                      <div
                          className={styles.validationError}>{validationErrors.studyCirclesAttendees}</div>
                  )}
                </div>
              </section>

              {/* Сайт и социальные сети */}
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Сайт и социальные сети</h3>

                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                        type="checkbox"
                        name="hasWebsite"
                        checked={formData.hasWebsite}
                        onChange={handleChange}
                        className={styles.checkbox}
                    />
                    Наличие сайта
                  </label>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Количество подписчиков <span
                      className={styles.required}>*</span>
                  </label>
                  <input
                      type="number"
                      name="socialNetworksCount"
                      value={formData.socialNetworksCount}
                      onChange={handleChange}
                      onWheel={handleWheel}
                      className={`${styles.input} ${validationErrors.socialNetworksCount
                          ? styles.inputError : ''}`}
                      min="0"
                      required
                  />
                  {validationErrors.socialNetworksCount && (
                      <div
                          className={styles.validationError}>{validationErrors.socialNetworksCount}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.sectionSubtitle}>Сообщества
                    регионального отделения ЛКСМ РФ</label>
                  <div className={styles.checkboxGroup}>
                    <label className={styles.checkboxLabel}>
                      <input
                          type="checkbox"
                          name="socialTelegram"
                          checked={formData.socialTelegram}
                          onChange={handleChange}
                          className={styles.checkbox}
                      />
                      Телеграм
                    </label>
                    <label className={styles.checkboxLabel}>
                      <input
                          type="checkbox"
                          name="socialVk"
                          checked={formData.socialVk}
                          onChange={handleChange}
                          className={styles.checkbox}
                      />
                      ВКонтакте
                    </label>
                    <label className={styles.checkboxLabel}>
                      <input
                          type="checkbox"
                          name="socialOk"
                          checked={formData.socialOk}
                          onChange={handleChange}
                          className={styles.checkbox}
                      />
                      Одноклассники
                    </label>
                    <label className={styles.checkboxLabel}>
                      <input
                          type="checkbox"
                          name="socialTiktok"
                          checked={formData.socialTiktok}
                          onChange={handleChange}
                          className={styles.checkbox}
                      />
                      TikTok
                    </label>
                    <label className={styles.checkboxLabel}>
                      <input
                          type="checkbox"
                          name="socialMax"
                          checked={formData.socialMax}
                          onChange={handleChange}
                          className={styles.checkbox}
                      />
                      Макс
                    </label>
                    <label className={styles.checkboxLabel}>
                      <input
                          type="checkbox"
                          name="socialOther"
                          checked={formData.socialOther}
                          onChange={handleChange}
                          className={styles.checkbox}
                      />
                      Иное
                    </label>
                  </div>
                </div>
              </section>

              {/* Контактная информация */}
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Контактная информация</h3>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Адрес регионального комитета <span
                      className={styles.required}>*</span>
                  </label>
                  <input
                      type="text"
                      name="committeeAddress"
                      value={formData.committeeAddress}
                      onChange={handleChange}
                      className={`${styles.input} ${validationErrors.committeeAddress
                          ? styles.inputError : ''}`}
                      required
                  />
                  {validationErrors.committeeAddress && (
                      <div
                          className={styles.validationError}>{validationErrors.committeeAddress}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Почта регионального отделения <span
                      className={styles.required}>*</span>
                  </label>
                  <input
                      type="email"
                      name="committeeEmail"
                      value={formData.committeeEmail}
                      onChange={handleChange}
                      className={`${styles.input} ${validationErrors.committeeEmail
                          ? styles.inputError : ''}`}
                      required
                  />
                  {validationErrors.committeeEmail && (
                      <div
                          className={styles.validationError}>{validationErrors.committeeEmail}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Ссылка на группу в ВКонтакте <span
                      className={styles.required}>*</span>
                  </label>
                  <input
                      type="url"
                      name="vkGroupLink"
                      value={formData.vkGroupLink}
                      onChange={handleChange}
                      className={`${styles.input} ${validationErrors.vkGroupLink
                          ? styles.inputError : ''}`}
                      required
                  />
                  {validationErrors.vkGroupLink && (
                      <div
                          className={styles.validationError}>{validationErrors.vkGroupLink}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Ссылка на канал в Телеграмм
                  </label>
                  <input
                      type="url"
                      name="telegramChannelLink"
                      value={formData.telegramChannelLink}
                      onChange={handleChange}
                      className={styles.input}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Ссылки на другие социальные сети и сайт
                  </label>
                  <textarea
                      name="otherSocialLinks"
                      value={formData.otherSocialLinks}
                      onChange={handleChange}
                      className={styles.textarea}
                      rows="3"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    ФИО первого секретаря <span
                      className={styles.required}>*</span>
                  </label>
                  <input
                      type="text"
                      name="firstSecretaryName"
                      value={formData.firstSecretaryName}
                      onChange={handleChange}
                      className={`${styles.input} ${validationErrors.firstSecretaryName
                          ? styles.inputError : ''}`}
                      required
                  />
                  {validationErrors.firstSecretaryName && (
                      <div
                          className={styles.validationError}>{validationErrors.firstSecretaryName}</div>
                  )}
                </div>
              </section>

              <div className={styles.formActions}>
                <button
                    type="submit"
                    className={styles.submitButton}
                    disabled={submitting}
                >
                  {submitting ? 'Отправка...' : 'Отправить отчет'}
                </button>
              </div>
            </form>

            {isSuccessModalOpen && (
                <div className={styles.modalOverlay}
                     onClick={() => setIsSuccessModalOpen(false)}>
                  <div className={styles.modal}
                       onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modalTitle}>Отчет отправлен</div>
                    <div className={styles.modalText}>
                      Спасибо! Ваш отчет успешно отправлен.
                    </div>
                    <div className={styles.modalActions}>
                      <button
                          type="button"
                          className={styles.modalButton}
                          onClick={() => setIsSuccessModalOpen(false)}
                      >
                        Ок
                      </button>
                    </div>
                  </div>
                </div>
            )}
          </div>
        </div>
      </div>
  );
};

export default StatReportForm;